import React, { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { mobileClasses, isMobile, formatForMobile } from '@/utils/mobileUtils'

// Icons
const ClipboardIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
)

const ClockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const ExclamationIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
)

const CameraIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

// Complaint Card Component
const ComplaintCard = ({ complaint, onUpdateStatus, onAddNote }) => {
  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-orange-100 text-orange-800',
      'on_hold': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const isOverdue = () => {
    if (!complaint.estimated_resolution_date) return false
    return new Date(complaint.estimated_resolution_date) < new Date()
  }

  return (
    <div className={`bg-white rounded-lg shadow ${mobileClasses.cardMobile} hover:shadow-md transition-shadow ${isOverdue() ? 'border-l-4 border-red-500' : ''}`}>
      <div className={`${mobileClasses.flexMobile} justify-between items-start mb-4`}>
        <div className="flex-1 min-w-0">
          <h3 className={`${mobileClasses.textMobileLarge} font-medium text-gray-900 truncate`}>{complaint.title}</h3>
          <p className="text-sm text-gray-500">#{complaint.complaint_number}</p>
        </div>
        <div className={`flex ${isMobile() ? 'flex-wrap gap-1 mt-2' : 'space-x-2'}`}>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
            {complaint.status.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(complaint.priority)}`}>
            {complaint.priority.toUpperCase()}
          </span>
          {isOverdue() && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
              OVERDUE
            </span>
          )}
        </div>
      </div>
      
      <p className={`text-gray-600 mb-4 ${mobileClasses.textMobile}`}>{formatForMobile.truncateText(complaint.description, 100)}</p>
      
      <div className={`${mobileClasses.flexMobile} justify-between items-center text-sm text-gray-500 mb-4`}>
        <span className="truncate">By: {complaint.first_name} {complaint.last_name}</span>
        <span className={`${isMobile() ? 'text-xs' : 'text-sm'}`}>{formatForMobile.formatDate(complaint.created_at)}</span>
      </div>

      {complaint.estimated_resolution_date && (
        <div className={`mb-4 p-3 rounded-lg ${isOverdue() ? 'bg-red-50' : 'bg-blue-50'}`}>
          <p className={`text-sm ${isOverdue() ? 'text-red-800' : 'text-blue-800'}`}>
            <strong>Due:</strong> {new Date(complaint.estimated_resolution_date).toLocaleDateString()}
          </p>
        </div>
      )}

      {complaint.resolution_notes && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-800">
            <strong>Notes:</strong> {complaint.resolution_notes}
          </p>
        </div>
      )}
      
      <div className={`${isMobile() ? 'grid grid-cols-2 gap-2' : 'flex space-x-2'}`}>
        {complaint.status === 'assigned' && (
          <Button
            onClick={() => onUpdateStatus(complaint.id, 'in_progress')}
            variant="primary"
            size={isMobile() ? "md" : "sm"}
            className={isMobile() ? "w-full" : ""}
          >
            Start Work
          </Button>
        )}
        
        {complaint.status === 'in_progress' && (
          <Button
            onClick={() => onUpdateStatus(complaint.id, 'resolved')}
            variant="primary"
            size={isMobile() ? "md" : "sm"}
            className={isMobile() ? "w-full" : ""}
          >
            Mark Resolved
          </Button>
        )}
        
        <Button
          onClick={() => onAddNote(complaint.id)}
          variant="outline"
          size={isMobile() ? "md" : "sm"}
          className={isMobile() ? "w-full" : ""}
        >
          Add Note
        </Button>
        
        <Button
          onClick={() => onUpdateStatus(complaint.id, 'on_hold')}
          variant="outline"
          size={isMobile() ? "md" : "sm"}
          className={isMobile() ? "w-full" : ""}
        >
          Hold
        </Button>
      </div>
    </div>
  )
}

// Add Note Modal Component
const AddNoteModal = ({ isOpen, onClose, onAddNote, complaintId }) => {
  const [note, setNote] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      await onAddNote(complaintId, note)
      setNote('')
      onClose()
    } catch (error) {
      console.error('Error adding note:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg ${mobileClasses.cardMobile} w-full ${mobileClasses.modalMobile}`}>
        <h2 className={`${mobileClasses.textMobileXLarge} font-bold mb-4`}>Add Progress Note</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Progress Note *
            </label>
            <textarea
              required
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={isMobile() ? 3 : 4}
              className={`${mobileClasses.inputMobile} border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Describe your progress on this complaint..."
            />
          </div>

          <div className={`${mobileClasses.flexMobile} ${isMobile() ? 'space-y-2' : 'justify-end space-x-3'} pt-4`}>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className={isMobile() ? "w-full" : ""}
              size={isMobile() ? "md" : "sm"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className={isMobile() ? "w-full" : ""}
              size={isMobile() ? "md" : "sm"}
            >
              {isLoading ? 'Adding...' : 'Add Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Main Worker Dashboard Component
const WorkerDashboard = () => {
  const { user } = useAuthStore()
  const [complaints, setComplaints] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('assigned')
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [selectedComplaintId, setSelectedComplaintId] = useState(null)

  // Fetch data from API
  useEffect(() => {
    const fetchComplaints = async () => {
      setIsLoading(true)
      try {
        const response = await fetch('/api/v1/complaints', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          // Filter complaints assigned to this worker
          const assignedComplaints = data.data?.filter(complaint => 
            complaint.assigned_to === user?.id
          ) || []
          setComplaints(assignedComplaints)
        } else {
          console.error('Error fetching complaints:', response.statusText)
          setComplaints([])
        }
      } catch (error) {
        console.error('Error fetching complaints:', error)
        setComplaints([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchComplaints()
  }, [user?.id])

  const handleUpdateStatus = async (complaintId, newStatus) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        const result = await response.json()
        // Update the complaint in the list
        setComplaints(complaints.map(complaint => 
          complaint.id === complaintId 
            ? { ...complaint, status: newStatus }
            : complaint
        ))
        alert('Status updated successfully!')
      } else {
        const error = await response.json()
        alert('Error updating status: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Error updating status. Please try again.')
    }
  }

  const handleAddNote = async (complaintId, note) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          resolution_notes: note,
          status: 'in_progress' // Update status to in_progress when adding notes
        })
      })

      if (response.ok) {
        const result = await response.json()
        // Update the complaint in the list
        setComplaints(complaints.map(complaint => 
          complaint.id === complaintId 
            ? { ...complaint, resolution_notes: note, status: 'in_progress' }
            : complaint
        ))
        alert('Note added successfully!')
      } else {
        const error = await response.json()
        alert('Error adding note: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note. Please try again.')
    }
  }

  const openNoteModal = (complaintId) => {
    setSelectedComplaintId(complaintId)
    setShowNoteModal(true)
  }

  const filteredComplaints = complaints.filter(complaint => {
    if (activeTab === 'all') return true
    return complaint.status === activeTab
  })

  const stats = [
    {
      title: 'My Tasks',
      value: complaints.length,
      icon: ClipboardIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Assigned',
      value: complaints.filter(c => c.status === 'assigned').length,
      icon: ClockIcon,
      color: 'bg-yellow-500'
    },
    {
      title: 'In Progress',
      value: complaints.filter(c => c.status === 'in_progress').length,
      icon: ExclamationIcon,
      color: 'bg-orange-500'
    },
    {
      title: 'Completed',
      value: complaints.filter(c => c.status === 'resolved').length,
      icon: CheckCircleIcon,
      color: 'bg-green-500'
    }
  ]

  const tabs = [
    { id: 'all', label: 'All Tasks', count: complaints.length },
    { id: 'assigned', label: 'Assigned', count: complaints.filter(c => c.status === 'assigned').length },
    { id: 'in_progress', label: 'In Progress', count: complaints.filter(c => c.status === 'in_progress').length },
    { id: 'resolved', label: 'Completed', count: complaints.filter(c => c.status === 'resolved').length }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={`max-w-7xl mx-auto ${mobileClasses.container} py-6 sm:py-8`}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className={`${mobileClasses.textMobileXLarge} font-bold text-gray-900`}>Worker Dashboard</h1>
          <p className={`mt-2 ${mobileClasses.textMobile} text-gray-600`}>
            Welcome back, {user?.first_name}! Manage your assigned tasks.
          </p>
        </div>

        {/* Stats Grid */}
        <div className={`${mobileClasses.grid4} gap-4 sm:gap-6 mb-6 sm:mb-8`}>
          {stats.map((stat, index) => (
            <div key={index} className={`bg-white rounded-lg shadow ${mobileClasses.cardMobileSmall}`}>
              <div className="flex items-center">
                <div className={`${isMobile() ? 'p-2' : 'p-3'} rounded-full ${stat.color}`}>
                  <stat.icon className={`${isMobile() ? 'w-5 h-5' : 'w-6 h-6'} text-white`} />
                </div>
                <div className={`${isMobile() ? 'ml-3' : 'ml-4'}`}>
                  <p className={`${isMobile() ? 'text-xs' : 'text-sm'} font-medium text-gray-600`}>{stat.title}</p>
                  <p className={`${isMobile() ? 'text-xl' : 'text-2xl'} font-semibold text-gray-900`}>{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tasks Section */}
        <div className="bg-white rounded-lg shadow">
          <div className={`${mobileClasses.cardMobileSmall} border-b border-gray-200`}>
            <h2 className={`${mobileClasses.textMobileLarge} font-medium text-gray-900`}>My Tasks</h2>
          </div>
          
          {/* Tabs */}
          <div className={`${mobileClasses.cardMobileSmall} border-b border-gray-200`}>
            <nav className={`${isMobile() ? 'flex flex-wrap gap-2' : 'flex space-x-8'}`}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${isMobile() ? 'px-3 py-2 text-xs' : 'py-2 px-1 text-sm'} border-b-2 font-medium ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {isMobile() ? `${tab.label} (${tab.count})` : `${tab.label} (${tab.count})`}
                </button>
              ))}
            </nav>
          </div>

          {/* Tasks List */}
          <div className={mobileClasses.cardMobile}>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className={`${isMobile() ? 'space-y-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'}`}>
                {filteredComplaints.map((complaint) => (
                  <ComplaintCard
                    key={complaint.id}
                    complaint={complaint}
                    onUpdateStatus={handleUpdateStatus}
                    onAddNote={openNoteModal}
                  />
                ))}
              </div>
            )}
            
            {!isLoading && filteredComplaints.length === 0 && (
              <div className="text-center py-8">
                <ClipboardIcon className={`${isMobile() ? 'w-10 h-10' : 'w-12 h-12'} text-gray-400 mx-auto mb-4`} />
                <p className={`${mobileClasses.textMobile} text-gray-500`}>No tasks found for this category.</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Note Modal */}
        <AddNoteModal
          isOpen={showNoteModal}
          onClose={() => setShowNoteModal(false)}
          onAddNote={handleAddNote}
          complaintId={selectedComplaintId}
        />
      </div>
    </div>
  )
}

export default WorkerDashboard
